import * as z from 'zod'

import { invariant } from '@epic-web/invariant'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useDiff } from '~/lib/diff/use-diff'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { useCRMetadata } from './use-cr-metadata'
import { calculateConflicts } from './use-active-conflicts-wizard'
import { branchIsMerged } from '../utils'
import {
  Branch,
  branchSchema,
  contentRequestSchema,
} from '@basehub/data/replicache/index'
import { useActiveSpaceId } from '../../explore/components/repo-store'
import { useQuery } from '@tanstack/react-query'

export const useCR = ({ crId }: { crId: string | null }) => {
  const { data: repoReplicacheData, isLoading: isLoadingReplicache } =
    useRepoReplicache()
  const spaceId = useActiveSpaceId() ?? repoReplicacheData?.spaceId
  const replicache = repoReplicacheData?.replicache
  const replicacheQueryEnabled =
    !isLoadingReplicache && Boolean(replicache) && !!crId

  const { data: crAndBranch } = useReplicacheQueryV2({
    key: ['cr', crId, 'replicache'],
    replicache,
    enabled: replicacheQueryEnabled,
    fetcher: async (tx) => {
      invariant(crId, 'not enabled')
      const cr = (await repoSpaceMutators.getContentRequest(tx, crId)) ?? null
      if (!cr) {
        return {
          cr: null,
          branch: null,
          baseBranch: null,
        }
      }
      const [branch, baseBranch] = await Promise.all([
        repoSpaceMutators.getBranch(tx, cr.headBranchId),
        repoSpaceMutators.getBranch(tx, cr.baseBranchId),
      ])
      return { cr, branch: branch ?? null, baseBranch: baseBranch ?? null }
    },
  })

  const fallbackQueryEnabled =
    !isLoadingReplicache &&
    !replicacheQueryEnabled &&
    !!spaceId &&
    !!crId &&
    !crAndBranch

  const fallback = useQuery({
    queryKey: ['cr', crId, 'fallback'],
    queryFn: async () => {
      invariant(spaceId, 'spaceId is undefined, this should not happen.')

      const branchesResponse = await fetch(
        `/api/ad-hoc/content-request/${crId}/?spaceId=${encodeURIComponent(
          spaceId
        )}`
      )
      try {
        const responseAsJson = z
          .object({
            branch: branchSchema,
            baseBranch: branchSchema,
            cr: contentRequestSchema,
          })
          .parse(await branchesResponse.json())
        return responseAsJson
      } catch (error) {
        return {
          cr: null,
          branch: null,
          baseBranch: null,
        }
      }
    },
    staleTime: Infinity,
    enabled: fallbackQueryEnabled,
  })

  if (!replicache) return fallback.data! ?? {}

  return crAndBranch! ?? {}
}

export const useBranchMergeDiff = (
  props: { branch: Branch | null } | { crId: string | null }
) => {
  const crId =
    'branch' in props
      ? (props.branch?.contentRequestIds?.[0] ?? null)
      : (props.crId ?? null)

  const { cr, baseBranch, branch: sourceBranch } = useCR({ crId })
  const branch = 'branch' in props ? props?.branch : sourceBranch
  const { data: headCommitBlockTree } = useCommitBlockTreeManager({
    commitId: branch?.headCommitId ?? undefined,
    rootBlockId: branch?.headCommit?.rootBlockId ?? undefined,
  })

  const { data: baseHeadCommitBlockTree } = useCommitBlockTreeManager({
    commitId: baseBranch?.headCommitId ?? undefined,
    rootBlockId: baseBranch?.headCommit?.rootBlockId ?? undefined,
  })

  const isLoadingBaseHeadCommitBlockTree = Boolean(
    !baseHeadCommitBlockTree && (baseBranch ? baseBranch.headCommitId : true)
  )

  const { data: commonAncestorQueryData } = useCRMetadata({ branch })
  const { data: commonAncestorCommitBlockTree } = useCommitBlockTreeManager({
    commitId: commonAncestorQueryData?.commonAncestor?.id,
    rootBlockId:
      commonAncestorQueryData?.commonAncestor?.rootBlockId ?? undefined,
  })

  const isLoadingCommonAncestorCommitBlockTree = Boolean(
    !commonAncestorCommitBlockTree &&
      (commonAncestorQueryData
        ? commonAncestorQueryData.commonAncestor?.id
        : true)
  )

  const diffResult = useDiff({
    diffType: 'merge',
    treeNew: headCommitBlockTree ?? undefined,
    treeOld: baseHeadCommitBlockTree
      ? baseHeadCommitBlockTree
      : isLoadingBaseHeadCommitBlockTree
        ? undefined
        : false,
    treeCommonAncestor: commonAncestorCommitBlockTree
      ? commonAncestorCommitBlockTree
      : isLoadingCommonAncestorCommitBlockTree
        ? undefined
        : false,
  })

  const { conflicts } = calculateConflicts({
    changesTree: diffResult.diff?.changesTree,
    conflictResolutions: cr?.conflictResolutions,
  })
  let status:
    | 'conflicts'
    | 'out-of-date'
    | 'no-changes'
    | 'merged'
    | 'archived'
    | 'ready' = 'ready'
  const ahead = commonAncestorQueryData?.ahead ?? 0
  const behind = commonAncestorQueryData?.behind ?? 0
  const merges = commonAncestorQueryData?.merges ?? []
  if (branch?.archivedAt) status = 'archived'
  else if (conflicts.length > 0) status = 'conflicts'
  else if (branchIsMerged({ ahead, merges })) status = 'merged'
  else if (!ahead) status = 'no-changes'
  else if (behind) status = 'out-of-date'
  else status = 'ready'

  return {
    diffResult,
    branch,
    cr,
    baseBranch,
    ahead,
    behind,
    diffCommitLineage: commonAncestorQueryData?.diffCommitLineage ?? null,
    merges,
    status,
    commonAncestorCommit: commonAncestorQueryData?.commonAncestor ?? null,
  }
}
